import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, TrendingUp, Trophy, Medal, Star, Globe, MapPin, Crown, Gauge, Swords, Zap } from "lucide-react";
import { api } from "../lib/api";

// Premium embossed gold medallion disc behind each stat icon.
const MEDALLION = {
  background: "radial-gradient(circle at 50% 30%, #F9E9A6 0%, #E5B842 44%, #B8860B 74%, #6b4e08 100%)",
  boxShadow: "0 5px 12px rgba(0,0,0,0.6), inset 0 2px 3px rgba(255,255,255,0.65), inset 0 -4px 6px rgba(0,0,0,0.5)",
  border: "1px solid rgba(255,230,150,0.55)",
};
const ENGRAVED = { color: "#4a3406", filter: "drop-shadow(0 1px 0 rgba(255,255,255,0.45))" };

function TrophyEmblem() {
  return (
    <span className="relative grid place-items-center w-14 h-14 rounded-full shrink-0" style={MEDALLION} data-testid="season-emblem">
      <Trophy className="w-6 h-6" strokeWidth={2.25} style={ENGRAVED} />
      <Star className="w-3 h-3 absolute left-1/2 -translate-x-1/2 top-[15px]" fill="#F9E9A6"
        style={{ color: "#F9E9A6", filter: "drop-shadow(0 1px 1px rgba(74,52,6,0.6))" }} />
    </span>
  );
}

function Cell({ Icon, value, label, accent = "text-slate-50", testid }) {
  return (
    <div className="flex flex-col items-start gap-2" data-testid={testid}>
      <span className="grid place-items-center w-14 h-14 rounded-full shrink-0" style={MEDALLION}>
        <Icon className="w-6 h-6" strokeWidth={2.25} style={ENGRAVED} />
      </span>
      <div className={`font-heading text-2xl sm:text-3xl font-black tabular-nums leading-none mt-1 ${accent}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function SectionHead({ children }) {
  return <h3 className="font-heading text-sm font-extrabold uppercase tracking-[0.2em] text-slate-300 mb-4">{children}</h3>;
}

const CARD = "rounded-xl bg-[#141414] border border-[rgba(212,175,55,0.18)] p-5 sm:p-6";

// ===== Per-ladder rating boxes (2x2 grid) =====
const LADDERS = [
  { key: "overall", label: "Overall", Icon: Crown, metric: null, to: "/rankings?entity=players&ranking=overall&scope=world" },
  { key: "rating", label: "ArenaBook", Icon: Gauge, metric: "Duels", to: "/rankings?entity=players&ranking=arenabook&scope=world" },
  { key: "challenge", label: "Challenge", Icon: Swords, metric: "Matches", to: "/rankings?entity=players&ranking=challenge&scope=world" },
  { key: "strike5", label: "Strike 5", Icon: Zap, metric: "Rounds", to: "/rankings?entity=players&ranking=strike5&scope=world" },
];

function ladderView(block) {
  const has = block ? (block.has_rating ?? block.eligible) : false;
  return {
    rating: has && block?.rating != null ? Math.round(block.rating) : null,
    world: block?.world_rank?.position ?? null,
    nat: block?.national_rank?.position ?? null,
    matches: block?.matches_count, // undefined for OVERALL (not tracked per-ladder)
  };
}

function RankCol({ Icon, label, pos }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1 min-w-0">
        <Icon className="w-3 h-3 shrink-0" /><span className="truncate">{label}</span>
      </div>
      <div className={`font-heading text-xl sm:text-2xl font-black tabular-nums leading-none ${pos != null ? "text-slate-50" : "text-slate-600"}`}>
        {pos != null ? `#${pos}` : "—"}
      </div>
    </div>
  );
}

export function LadderRatingGrid({ player }) {
  const countryName = (player?.country?.name || "National").toUpperCase();
  return (
    <div className="mt-3 sm:mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="passport-ladder-grid">
      {LADDERS.map(({ key, label, Icon, metric, to }) => {
        const v = ladderView(player?.[key]);
        return (
          <Link key={key} to={to} data-testid={`ladder-${key}`} aria-label={`${label} individual leaderboard`}
            className={`${CARD} block transition-colors hover:border-[rgba(212,175,55,0.55)] focus:outline-none focus:ring-2 focus:ring-[#E5B842]/50`}>
            <div className="flex items-center gap-2 mb-4 min-w-0">
              <span className="grid place-items-center w-8 h-8 rounded-full shrink-0" style={MEDALLION}>
                <Icon className="w-4 h-4" strokeWidth={2.4} style={ENGRAVED} />
              </span>
              <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#C8A24A] truncate">{label} · Individual</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">Rating</div>
                {v.rating != null
                  ? <div className="font-heading italic text-2xl sm:text-3xl font-black tabular-nums leading-none" style={{ color: "#D4AF37" }}>{v.rating}</div>
                  : <div className="font-heading text-sm font-bold text-slate-500 leading-none pt-1.5">Unrated</div>}
              </div>
              <RankCol Icon={Globe} label="World" pos={v.world} />
              <RankCol Icon={MapPin} label={countryName} pos={v.nat} />
            </div>
            {v.matches != null && (
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-600 mt-3">{metric} played: {v.matches}</div>
            )}
          </Link>
        );
      })}
    </div>
  );
}

export function PerformanceOverviewBox({ wsbfId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/players/${wsbfId}/performance-summary`).then((r) => { if (on) setD(r.data); }).catch(() => { if (on) setD({}); });
    return () => { on = false; };
  }, [wsbfId]);

  const winRate = d && d.win_rate != null ? `${d.win_rate}%` : "—";
  const roi = d && d.roi != null ? `${d.roi > 0 ? "+" : ""}${d.roi}%` : "—";
  const tournaments = d && d.tournaments != null ? d.tournaments : "—";
  const podium = d && d.podium != null ? d.podium : "—";

  return (
    <div className={CARD} data-testid="box-performance-overview">
      <SectionHead>Performance Overview</SectionHead>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
        <Cell Icon={Target} value={winRate} label="Win Rate" accent="text-[#E5B842]" testid="perf-win-rate" />
        <Cell Icon={TrendingUp} value={roi} label="Prediction ROI" testid="perf-roi" />
        <Cell Icon={Trophy} value={tournaments} label="Tournaments" testid="perf-tournaments" />
        <Cell Icon={Medal} value={podium} label="Podium Finishes" testid="perf-podium" />
      </div>
    </div>
  );
}

export function CurrentSeasonBox({ wsbfId }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/player/${wsbfId}/season`).then((r) => { if (on) setS(r.data?.summary || {}); }).catch(() => { if (on) setS({}); });
    return () => { on = false; };
  }, [wsbfId]);

  const total = s?.season_total_points ?? 0;
  const pos = s?.position ?? null;
  const earned = s?.earned_best8_points ?? 0;
  const carry = s?.carry_over_points ?? 0;
  const pct = carry > 0 ? Math.min(100, Math.round((earned / carry) * 100)) : (earned > 0 ? 100 : 0);

  return (
    <div className={CARD} data-testid="box-current-season">
      <SectionHead>Current Season</SectionHead>
      <div className="flex items-center gap-3">
        <TrophyEmblem />
        <div>
          <div className="flex items-baseline gap-1.5">
            <span data-testid="season-total-points" className="font-heading italic font-black tabular-nums leading-none text-4xl sm:text-5xl" style={{ color: "#D4AF37" }}>{total}</span>
            <span className="font-heading italic font-bold text-lg sm:text-xl" style={{ color: "#D4AF37" }}>pts</span>
          </div>
          <div data-testid="season-position" className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-1.5">{pos ? `#${pos} Overall` : "Unranked · Season Race"}</div>
        </div>
      </div>
      <div className="mt-5">
        <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 mb-1.5">
          <span>This season (Best-8): {earned} pts</span>
          <span className="tabular-nums">{pct}%</span>
        </div>
        {/* Raised charcoal track with inner shadow */}
        <div className="relative h-3.5 rounded-full overflow-hidden" data-testid="season-progress"
          style={{ background: "#0b0b0b", boxShadow: "inset 0 2px 5px rgba(0,0,0,0.85), inset 0 -1px 0 rgba(255,255,255,0.04)" }}>
          {/* Embossed gold fill + shine stripe */}
          <div className="h-full rounded-full relative" style={{
            width: `${pct}%`,
            background: "linear-gradient(to bottom, #F9E9A6 0%, #E5B842 48%, #C8961F 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -2px 3px rgba(0,0,0,0.35), 0 0 10px rgba(229,184,66,0.45)",
            transition: "width 0.5s ease",
          }}>
            <div className="absolute left-0 right-0 top-0 h-1/2 rounded-t-full" style={{ background: "linear-gradient(to bottom, rgba(255,255,255,0.45), transparent)" }} />
          </div>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-slate-600 mt-1.5">Previous season (carry-over): {carry} pts</div>
      </div>
    </div>
  );
}
